import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { IxmUserauthV1ClearingDetails } from '@interxfi/types';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import type { FormConfirmationGroup } from '../../../../components/form/confirmation/form-confirmation';
import { getFixedOrMutableIdSchema } from '../../../../components/form/schema';
import { addDefinedValues } from '../../../../util/has-value';

export const programClearingDetailsFormSchema = z.object({
  programCode: getFixedOrMutableIdSchema(
    'Program Code is required',
    z.string().regex(/^[A-Z]{4}$/, { error: 'Program Code must be exactly four uppercase letters' }),
  ),
  shortName: z.string().nonempty('Short Name is required'),
  mpid: z
    .string()
    .regex(/^[A-Z]{4}$/, 'MPID must be exactly 4 uppercase letters')
    .or(z.literal('')),
  taxVendorId: z.string().nonempty('Tax Vendor ID is required'),
  proxyVendorId: z.string().nonempty('Proxy Vendor ID is required'),
});

export type ProgramClearingDetailsFormValues = z.infer<typeof programClearingDetailsFormSchema>;

export const defaultProgramClearingDetailsFormValues: ProgramClearingDetailsFormValues = {
  programCode: { kind: 'mutable', value: '' },
  shortName: '',
  mpid: '',
  taxVendorId: '',
  proxyVendorId: '',
};

export function getInitialFormValuesForClearingDetails(
  data: IxmUserauthV1ClearingDetails | undefined,
  programCode?: string,
): ProgramClearingDetailsFormValues {
  const programCodeValue = programCode ? ({ kind: 'fixed', value: programCode } as const) : ({ kind: 'mutable', value: '' } as const);

  if (!data) {
    return {
      ...defaultProgramClearingDetailsFormValues,
      programCode: programCodeValue,
    };
  }

  return {
    programCode: programCodeValue,
    shortName: data.shortName || '',
    mpid: data.mpid || '',
    taxVendorId: data.taxVendorId || '',
    proxyVendorId: data.proxyVendorId || '',
  };
}

export function formValuesToClearingDetails(values: ProgramClearingDetailsFormValues): IxmUserauthV1ClearingDetails {
  // Note: programCode is not included as it's not part of IxmUserauthV1ClearingDetails, but rather part of the main program object.
  // It's only submitted when creating a new program, and its value will be mapped by a parent form.

  return addDefinedValues<IxmUserauthV1ClearingDetails>(
    {
      shortName: values.shortName,
      taxVendorId: values.taxVendorId,
      proxyVendorId: values.proxyVendorId,
    },
    {
      mpid: values.mpid,
    },
  );
}

export function mapClearingDetailsFormValuesToFormConfirmationDetails(values: ProgramClearingDetailsFormValues): FormConfirmationGroup {
  return {
    id: 'clearingDetails',
    list: {
      items: [
        getReadonlyListItemString('programCode', 'Program Code', values.programCode.value),
        getReadonlyListItemString('shortName', 'Short Name', values.shortName),
        getReadonlyListItemString('mpid', 'MPID', values.mpid),
        getReadonlyListItemString('taxVendorId', 'Tax Vendor ID', values.taxVendorId),
        getReadonlyListItemString('proxyVendorId', 'Proxy Vendor ID', values.proxyVendorId),
      ],
    },
  };
}

interface ProgramClearingDetailsFormProps {
  lens: Lens<ProgramClearingDetailsFormValues>;
  showTooltips?: boolean;
}

export function ProgramClearingDetailsForm({ lens, showTooltips }: ProgramClearingDetailsFormProps) {
  const isProgramCodeFixed = useWatch(lens.focus('programCode.kind').interop()) === 'fixed';

  return (
    <VerticalStack gap={4}>
      <AppTextInputWithLens
        disabled={isProgramCodeFixed}
        info={showTooltips ? 'Must be four uppercase letters. Used as a prefix for system generated Display IDs.' : undefined}
        infoWidth={88}
        label="Program Code"
        lens={lens.focus('programCode.value')}
        required
      />
      <AppTextInputWithLens
        info={
          showTooltips
            ? '16-character maximum. This is the name for the Program that may appear on a customer’s bank statement for ACH transfers.'
            : undefined
        }
        infoWidth={220}
        label="Short Name"
        lens={lens.focus('shortName')}
        required
      />
      <AppTextInputWithLens
        info={showTooltips ? 'Must be four uppercase letters. Broker-dealers Market Participant Identifier assigned by FINRA.' : undefined}
        infoWidth={220}
        label="MPID"
        lens={lens.focus('mpid')}
      />
      <AppTextInputWithLens
        info={showTooltips ? 'Maps accounts to the correct branding configuration for tax forms.' : undefined}
        infoWidth={220}
        label="Tax Vendor ID"
        lens={lens.focus('taxVendorId')}
        required
      />
      <AppTextInputWithLens
        info={showTooltips ? 'Maps accounts to the correct branding configuration for shareholder communications.' : undefined}
        infoWidth={220}
        label="Proxy Vendor ID"
        lens={lens.focus('proxyVendorId')}
        required
      />
    </VerticalStack>
  );
}
